import {useMemo} from 'react'

import type {SingleSelectValue} from '../../../api/columns/contracts/single-select'
import type {ColumnData} from '../../../api/columns/contracts/storage'
import {useFilteredItems} from '../../../features/filtering/hooks/use-filtered-items'
import {getGroupByFieldId} from '../../../features/grouping/get-group-by-field'
import {buildGroupingConfiguration} from '../../../features/grouping/grouping-metadata-configurations'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useVerticalGroups} from '../../../features/grouping/hooks/use-vertical-groups'
import {useSortedItems} from '../../../features/sorting/hooks/use-sorted-items'
import {useViews} from '../../../hooks/use-views'
import type {ColumnModel} from '../../../models/column-model'
import type {Group} from '../../../models/group'
import type {HorizontalGrouping} from '../../../models/horizontal-group'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {canVerticalGroup, MissingVerticalGroupId} from '../../../models/vertical-group'
import {useAllColumns} from '../../../state-providers/columns/use-all-columns'
import {Resources} from '../../../strings'
import {buildGetGroupedRowsFunctionForBoard} from '../grouping'

const defaultGroupByFieldOptions: Array<Group> = []
/**
 * Filters and groups items depending on the current view.
 * Steps:
 * 1. Apply the current filter to the full list of items.
 * 2. If the current view is not grouped, we will fall back to the `Status` field.
 * 3. If the current view is grouped, we will create a list of groups, each with a list of items, based on the vertical grouping field.
 * @returns An array of items grouped by the vertical group they belong to
 */
export function useBoardItems() {
  const {filteredItems} = useFilteredItems()
  const {groupedByColumnId, groupByFieldOptions = defaultGroupByFieldOptions} = useVerticalGroups()
  const {groupedByColumn: horizontalGroupedByColumn, collapsedGroups} = useHorizontalGroupedBy()
  const {sortUngroupedItems} = useSortedItems()
  const {missingRequiredColumnData} = useViews()
  const {allColumns} = useAllColumns()
  const compatibleColumns = useMemo(() => allColumns.filter(canVerticalGroup), [allColumns])

  const groupByFieldId = useMemo(
    () => getGroupByFieldId(compatibleColumns, groupedByColumnId),
    [compatibleColumns, groupedByColumnId],
  )

  const groupByField = useMemo(() => allColumns.find(c => c.id === groupByFieldId), [allColumns, groupByFieldId])

  const groupingConfig = useColumnsWithGroupingConfiguration()

  const groupedItems: HorizontalGrouping = useMemo(() => {
    const allItemsByVerticalGroup: Readonly<{
      [k: string]: ReadonlyArray<MemexItemModel>
    }> = Object.fromEntries(
      Object.entries(
        groupItemsByField(
          filteredItems,
          groupByFieldId,
          groupByFieldOptions,
          missingRequiredColumnData,
          MissingVerticalGroupId,
        ),
      ).map(([fieldValue, items]) => {
        return [fieldValue, sortUngroupedItems(items)] as const
      }),
    )
    if (horizontalGroupedByColumn) {
      const groupConfig = groupingConfig[horizontalGroupedByColumn?.id]

      const horizontalGroups =
        groupConfig
          ?.groupingConfig?.(filteredItems)
          .map(horizontalGroup => {
            const itemsByVerticalGroup = Object.fromEntries(
              Object.entries(
                groupItemsByField(
                  horizontalGroup.rows,
                  groupByFieldId,
                  groupByFieldOptions,
                  missingRequiredColumnData,
                  MissingVerticalGroupId,
                ),
              ).map(([fieldValue, items]) => {
                return [fieldValue, sortUngroupedItems(items)] as const
              }),
            )
            return {
              ...horizontalGroup,
              isCollapsed: collapsedGroups.includes(horizontalGroup.value),
              itemsByVerticalGroup,
            }
          })
          .filter(horizontalGroup => horizontalGroup.rows.length > 0) ?? []

      if (horizontalGroups.length > 0) {
        return {
          isHorizontalGrouped: true as const,
          horizontalGroupedByColumn,
          allItemsByVerticalGroup,
          horizontalGroups,
        }
      }
    }

    return {
      isHorizontalGrouped: false as const,
      allItemsByVerticalGroup,
      horizontalGroups: [
        {
          value: Resources.undefined,
          isCollapsed: false,
          itemsByVerticalGroup: allItemsByVerticalGroup,
          rows: filteredItems,
        },
      ],
    }
  }, [
    horizontalGroupedByColumn,
    groupingConfig,
    filteredItems,
    collapsedGroups,
    groupByFieldId,
    groupByFieldOptions,
    missingRequiredColumnData,
    sortUngroupedItems,
  ])

  return useMemo(() => {
    return {
      groupByFieldOptions,
      groupByFieldId,
      groupByField,
      compatibleColumns,
      filteredItems,
      groupedItems,
    }
  }, [groupByFieldOptions, groupByFieldId, groupByField, compatibleColumns, filteredItems, groupedItems])
}

/**
 * Utility function that actually performs the grouping of items, based on their values for the
 * group by field.
 * Items without a value for the group by field will be grouped together in a group with the id of the
 * `missingGroupId` parameter.
 * Currently only supports single-select and iteration fields.
 *
 * @param items Flat, ungrouped list of memex items this method will group
 * @param groupByFieldId The id of the field to group by
 * @param fieldOptions An array of valid groups associated with the field to group by
 * @param missingRequiredColumnData Whether or not we are currently missing column values for the field to group by
 * @param missingGroupId The id of a hard-coded group to use for items that do not have any group by value
 * @returns A mapping of group ids to arrays of items belonging to that group
 */
function groupItemsByField<TColumnId extends keyof ColumnData>(
  items: Readonly<Array<MemexItemModel>>,
  groupByFieldId: TColumnId,
  fieldOptions: Array<Group>,
  missingRequiredColumnData: boolean,
  missingGroupId: string,
) {
  const groupedBy: {[fieldValue: string]: Array<MemexItemModel>} = Object.fromEntries([
    [missingGroupId, []],
    ...fieldOptions.map<[string, []]>(option => [option.id, []]),
  ])

  if (missingRequiredColumnData) {
    return groupedBy
  }
  for (const item of items) {
    const fieldValue = getGroupByFieldValueForItem(item.columns, groupByFieldId, missingGroupId)

    if (fieldValue in groupedBy || fieldValue === missingGroupId) {
      groupedBy[fieldValue]?.push(item)
    }
  }
  return groupedBy
}

function getGroupByFieldValueForItem<TColumnId extends keyof ColumnData>(
  columnData: ColumnData,
  groupByFieldId: TColumnId,
  missingGroupId: string,
): string {
  if (groupByFieldId === -1) {
    return missingGroupId
  }
  const singleSelectValue = columnData[groupByFieldId] as SingleSelectValue
  if (!singleSelectValue || !singleSelectValue.id) {
    return missingGroupId
  }

  return singleSelectValue.id
}

const boardGroupingConfig = buildGroupingConfiguration<Readonly<MemexItemModel>>(row => row.columns)
function getColumnBehaviors(columnModel: Pick<ColumnModel, 'dataType'>) {
  return boardGroupingConfig[columnModel.dataType]
}
const useColumnsWithGroupingConfiguration = () => {
  const {allColumns} = useAllColumns()
  const groupConfigurations = useMemo(() => {
    return Object.fromEntries(
      allColumns.map(column => {
        const behaviors = getColumnBehaviors(column)
        return [
          column.id,
          {
            ...column,
            groupingConfig: behaviors ? buildGetGroupedRowsFunctionForBoard(column, behaviors) : undefined,
          },
        ] as const
      }),
    )
  }, [allColumns])

  return groupConfigurations
}
